/** Papéis reconhecidos pelo JWT / Identity da API. */
export const USER_ROLES = {
  master: 'Master',
  admin: 'Admin',
  leader: 'Leader',
  member: 'Member',
  user: 'User',
  clientOwner: 'ClientOwner',
  consumer: 'Consumer'
} as const;

export type UserRole = (typeof USER_ROLES)[keyof typeof USER_ROLES];

/** Nomenclatura oficial (Anuário V2 / CRIACAO_DE_CONTAS). */
export const ROLE_LABELS: Record<string, string> = {
  Master: 'Super Admin Master',
  Admin: 'Administrador',
  Leader: 'Líder de Equipe',
  Member: 'Membro de Equipe',
  User: 'Membro de Equipe',
  ClientOwner: 'Empresário',
  Consumer: 'Consumidor'
};

export const STAFF_ROLES: UserRole[] = [USER_ROLES.master, USER_ROLES.admin];
export const ROUTE_ADMIN_ROLES: UserRole[] = [USER_ROLES.master, USER_ROLES.admin, USER_ROLES.leader];
export const ASSIGNABLE_ROLES: UserRole[] = [USER_ROLES.admin, USER_ROLES.leader, USER_ROLES.member];

export function creatableRolesFor(actorRole?: string | null, isMaster = false): UserRole[] {
  if (isMaster || actorRole === USER_ROLES.master) {
    return [USER_ROLES.master, USER_ROLES.admin, USER_ROLES.leader, USER_ROLES.member];
  }
  if (actorRole === USER_ROLES.admin) {
    return [USER_ROLES.leader, USER_ROLES.member];
  }
  if (actorRole === USER_ROLES.leader) {
    return [USER_ROLES.member];
  }
  return [];
}

export function roleLabel(role?: string | null): string {
  return ROLE_LABELS[role ?? ''] ?? ROLE_LABELS['Member'];
}

export function isStaffRole(role?: string | null): boolean {
  return role === USER_ROLES.master || role === USER_ROLES.admin;
}

/** Cadastro manual de empresa (lead) na aba Clientes — exclusivo administrador. */
export function canRegisterManualCompany(role?: string | null): boolean {
  return isStaffRole(role);
}

export function canManageRoutes(role?: string | null): boolean {
  return isStaffRole(role) || role === USER_ROLES.leader;
}

/** Lista de clientes (escopo filtrado na API para líder e membro). */
export function canViewClients(role?: string | null): boolean {
  return (
    isStaffRole(role) ||
    role === USER_ROLES.leader ||
    role === USER_ROLES.member ||
    role === USER_ROLES.user
  );
}

/** Ações destrutivas ou globais na aba Clientes (ex.: excluir, cadastro manual). */
export function canManageClients(role?: string | null): boolean {
  return isStaffRole(role);
}

/** Edição operacional de clientes no escopo (ficha, vitrine, chaves). */
export function canEditClientsInScope(role?: string | null): boolean {
  return canViewClients(role);
}

/** Painel /campo/rotas: gestores editam; membros só veem rotas atribuídas (API). */
export function canAccessRouteAdminPanel(role?: string | null): boolean {
  return (
    canManageRoutes(role) ||
    role === USER_ROLES.member ||
    role === USER_ROLES.user
  );
}
